import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { ClipboardList, Calendar, Clock, PenTool, Image, CheckCircle, XCircle, User, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const STATUS = {
  pending: { label: 'Pendiente', bg: 'bg-amber-50 text-amber-700' },
  approved: { label: 'Aprobado', bg: 'bg-green-50 text-green-700' },
  rejected: { label: 'Rechazado', bg: 'bg-red-50 text-red-700' },
};

export default function WorkReportsCompanyPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailReport, setDetailReport] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      const res = await API.get('/work-reports');
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const updateStatus = async (id, status) => {
    try {
      await API.put(`/work-reports/${id}/status?status=${status}`);
      setReports(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      if (detailReport?.id === id) setDetailReport(prev => ({ ...prev, status }));
    } catch (err) { console.error(err); }
  };

  const pendingCount = reports.filter(r => r.status === 'pending').length;

  const exportPDF = async () => {
    try {
      const res = await API.get('/work-reports/export/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'partes_trabajo.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="work-reports-company-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Partes de Trabajo</h1>
          <p className="text-sm text-gray-500 mt-0.5">{reports.length} partes — {pendingCount} pendientes de revision</p>
        </div>
        {reports.length > 0 && (
          <Button variant="outline" className="border-orange-200 text-[#FF6B35] hover:bg-orange-50" onClick={exportPDF} data-testid="export-reports-pdf">
            <FileDown className="w-4 h-4 mr-2" /> Exportar PDF
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {reports.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-400">
            <ClipboardList className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p>No hay partes de trabajo</p>
          </div>
        ) : reports.map(r => {
          const s = STATUS[r.status] || STATUS.pending;
          return (
            <div key={r.id} onClick={() => setDetailReport(r)} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-orange-200 transition-all cursor-pointer">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 flex items-center gap-1"><User className="w-3.5 h-3.5 text-gray-400" />{r.user_name}</span>
                    <Badge className={`${s.bg} text-xs`}>{s.label}</Badge>
                    {r.photos?.length > 0 && <Badge className="bg-blue-50 text-blue-600 text-xs"><Image className="w-3 h-3 mr-1" />{r.photos.length}</Badge>}
                    {r.signature_path && <PenTool className="w-3.5 h-3.5 text-green-500" />}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-1">{r.description}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{r.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.start_time} - {r.end_time}</span>
                  </div>
                </div>
                {r.status === 'pending' && (
                  <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" className="text-green-600 hover:bg-green-50 h-8 w-8 p-0" onClick={() => updateStatus(r.id, 'approved')} data-testid={`approve-${r.id}`}>
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50 h-8 w-8 p-0" onClick={() => updateStatus(r.id, 'rejected')} data-testid={`reject-${r.id}`}>
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detailReport} onOpenChange={() => setDetailReport(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Parte de {detailReport?.user_name}</DialogTitle>
            <DialogDescription>{detailReport?.date} — {detailReport?.start_time} a {detailReport?.end_time}</DialogDescription>
          </DialogHeader>
          {detailReport && (
            <div className="space-y-4">
              <Badge className={`${(STATUS[detailReport.status] || STATUS.pending).bg}`}>{(STATUS[detailReport.status] || STATUS.pending).label}</Badge>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{detailReport.description}</p>
              {detailReport.photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {detailReport.photos.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="rounded-lg overflow-hidden border border-gray-200 aspect-square block">
                      <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {detailReport.signature_path && <div className="flex items-center gap-2 text-xs text-green-600"><PenTool className="w-3.5 h-3.5" />Firmado digitalmente</div>}
              {detailReport.status === 'pending' && (
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => updateStatus(detailReport.id, 'approved')}>
                    <CheckCircle className="w-4 h-4 mr-2" />Aprobar
                  </Button>
                  <Button variant="outline" className="flex-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => updateStatus(detailReport.id, 'rejected')}>
                    <XCircle className="w-4 h-4 mr-2" />Rechazar
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
